// Analytics, round 4 wireframe (package 7): what GET /p/:projectId/analytics
// adds to AnalyticsV3 (stakeholders.ts). Aggregates only: no names of people.
// Round 5 (AN5): the team's own page shows every group, small ones too; the
// under-5 rule (small-groups.ts) stays on exports and the funder view.
import { z } from "zod";
import type { StatusChip } from "./common";

/** One country of "Engaged stakeholders by country". code: ISO 3166 alpha-2 (XK = Kosovo); null for a merged or hidden row. */
export type AnalyticsCountry = {
  code: string | null;
  label: string;
  people: number | null;
  womenPct: number | null;
  hidden?: boolean;
  /**
   * UI-AN5 (retest 1 Nov), the map's country panel, on a country's own row: the organisations of
   * its people, the events its people took part in (an event has no country of its own, only the
   * free text of its venue), and their average overall rating with the number of answers
   * (satisfaction null under 5 answers: feedback is anonymous, a small country would give one away).
   */
  organisations?: number;
  events?: number;
  /** AN-F4: times its people took part (one per person per event). */
  participations?: number;
  /** AN-F4: the results its people contributed to (the results of the activities of the events they took part in), in the logframe's order. */
  results?: { id: string; code: string; label: string }[];
  satisfaction?: number | null;
  /** Round 3 review: null under 5 answers (SMALL_GROUP_MIN), like satisfaction: a small count is not shown either. */
  responses?: number | null;
};

/**
 * One row of a tag group: participants with the tag (on the person, or on their organisation when not on the person).
 * tagId: opens the stakeholders list filtered by the tag (`?tag=<id>`); absent on the hidden "Other tags" row.
 */
export type AnalyticsTagRow = { tagId?: string; label: string; people: number | null; sharePct: number | null; womenPct: number | null; hidden?: boolean };

export type AnalyticsMore = {
  /** The buttons: Whole project, the current year, the current reporting period, Last 3 months. */
  periodButtons: { value: string; label: string }[];
  /** The same period as an Activities filter (`/activities?period=`), so a figure opens the activities behind it. */
  activitiesPeriod: string;
  figures: {
    /** Different people who took part. */
    engaged: number;
    organisations: number;
    /** Organisations taking part for the first time in the period ("+11 new"); for the whole project, this quarter. */
    newOrganisations: number;
    newOrganisationsLabel: string;
    womenPct: number | null;
    /**
     * EV6 (retest 30 Oct): the people of the period by gender, each counted once (the latest answer),
     * the base of womenPct (women and men) and of "Who takes part", so the page has one definition.
     */
    gender: { women: number; men: number; other: number; notRecorded: number };
    /** The events' target share of women (their average); null = none set. */
    womenTargetPct: number | null;
    /** Share of the people who came to 2 or more events. */
    returningPct: number | null;
    /** Average overall feedback, 1 to 5, one decimal. */
    satisfaction: number | null;
    responses: number;
    /** AN7: the indicators' current status (as on Results today), whatever period is picked. */
    indicatorsOnTrack: number;
    indicatorsBehind: number;
    /** AN3: indicators whose latest reading is missing or not confirmed. */
    indicatorsNotConfirmed: number;
    indicatorsTotal: number;
  };
  countries: AnalyticsCountry[];
  /**
   * People taking part per month: new to the project that month, or returning. AN-F3: label with the year
   * ("Oct 25"); the months run to the current month when the period reaches today; events: the events held
   * that month (started that month, up to today), for the bar's pop-up.
   */
  months: { month: string; label: string; newPeople: number; returning: number; events?: AnalyticsMonthEvent[] }[];
  /**
   * AN7, the events of the period step by step: invited = an invitation was sent from Convenor; saidYes = of
   * those, answered yes; checkedIn = took part (checked in or on an attendance list, walk-ins included);
   * feedback = feedback forms sent back; otherWays = registered or present without an invitation from Convenor
   * (the registration form, added by the team, confirmed outside the app, walk-ins).
   */
  funnel: { invited: number; saidYes: number; checkedIn: number; feedback: number; otherWays: number; /** UI-AN6: of those who took part, people not invited through Convenor. */ tookPartNotInvited?: number };
  /** A7, "Do people come back?": the people of the period by how many events they came to (up to the end of the period). */
  comeBack?: { once: number; twice: number; threeToFour: number; fiveOrMore: number };
  /** A1: the choices of the filters (every result; the countries people came from), whatever is filtered. */
  filterOptions?: { results: { id: string; label: string }[]; countries: { code: string; label: string }[] };
  /** A1: the filters applied (result id, ISO country, kind of event); absent = none. */
  filters?: { result?: string; country?: string; kind?: string };
  /**
   * A14: spent (as Home counts it: reported and not yet reported) against the contract total, and how much
   * of the project's time has gone. Null when not known. AN-F8: no longer a card on Analytics (the Budget's
   * "Budget insights" shows it); kept for the money sentence of "For your report".
   */
  spending?: { spentCents: number; budgetCents: number | null; spentPct: number | null; timePct: number | null } | null;
  /** A4, "How the reach grew": people reached per country, counted up month by month (each person once, from their first event). */
  reachGrowth?: AnalyticsReachGrowth;
  /** A8, "Gone quiet": people who came 3 times or more and have not been seen for 6 months (the team's page only: names). */
  goneQuiet?: AnalyticsGoneQuiet | null;
  /** A9, "Balance against targets": women, each stakeholder type and the partner countries, against the project's targets. */
  balance?: AnalyticsBalance;
  /** A10, "Not reached yet": partner countries and groups with a target that have nobody or few people. */
  notReached?: AnalyticsNotReached;
  /** A11, "The year at a glance": the days of one calendar year with events or report deadlines. */
  year?: AnalyticsYear;
  /** A12, "Indicator journeys": each indicator from baseline through its readings to its target. */
  journeys?: AnalyticsJourney[];
  /** A13, "Training series, edition by edition": the trainings of one line of work, with their scores. */
  trainingSeries?: AnalyticsTrainingSeries[];
  /** Tag groups marked "use in analytics" (empty while tags are not set up). */
  tagGroups: { id: string; name: string; rows: AnalyticsTagRow[] }[];
  /**
   * The indicators with Results' own figures for the period (AN1), in the logframe's order, each with its
   * result (AN8 groups them). Texts carry the unit; baselineText is "not set" without a baseline (LF8).
   */
  indicatorRows: {
    id: string;
    code: string;
    name: string;
    /** The short name, else the name cut to 60 characters. */
    shortName: string;
    /** Where the figure comes from (means of verification). */
    source: string;
    current: number | null;
    target: number | null;
    baselineText: string;
    currentText: string;
    targetText: string;
    /** 0 to 100, from baseline to target. */
    progressPct: number | null;
    /** Workbook 27 Oct: no reading yet, below start, above target, or plain progress. */
    barState?: "no_reading" | "below_start" | "above_target" | "progress";
    status: StatusChip;
    /** The result it measures; null = not linked to one. */
    result: { id: string; code: string; label: string } | null;
    /** "8 lists from check-in" when the value is counted from the record. */
    fromRecord: string | null;
    /** The date of the reading the value comes from. */
    readingDate: string | null;
  }[];
  /** Organisations with people taking part, the most first (at most 5). */
  organisations: { id: string | null; name: string; country: string | null; people: number; activities: number }[];
  /** Published deliverables, the most viewed first (at most 5). */
  publications: { title: string; code: string | null; kind: string; views: number; downloads: number; path: string | null }[];
  /** Section 5: one row per event of the period, the newest first ("which formats work", value for money). */
  events: AnalyticsEventRow[];
  /** Section 5: who keeps coming back (2 or more events), per country and organisation; rows with someone returning. */
  returning: {
    countries: { code: string | null; label: string; returning: number; people: number }[];
    organisations: { id: string | null; name: string; returning: number; people: number }[];
  };
};

export type AnalyticsEventRow = {
  id: string;
  /** A16: the kind of event (workshop, training…), for "What an event costs". */
  kind?: import("../enums").EventKind;
  title: string;
  code: string | null;
  startDate: string;
  /** The event's activity page; null for an event without an activity. */
  path: string | null;
  /** Took part (checked in or on an attendance list). */
  participants: number;
  /** Women, of the people who said woman or man. */
  womenPct: number | null;
  /** Average overall feedback, 1 to 5, one decimal. */
  satisfaction: number | null;
  responses: number;
  /** Feedback forms sent back, of the participants. */
  responseRatePct: number | null;
  /** What the activity costs, as the Activities list shows it (EV4): the final cost once closed, else the ToR, else the estimate. */
  costCents: number | null;
  costPerParticipantCents: number | null;
};

/** A4: months (YYYY-MM) and, per country, the people reached by the end of each month. */
export type AnalyticsReachGrowth = {
  months: { month: string; label: string }[];
  /** counts[i]: people from the country reached by the end of months[i]; null = under 5 (exports). */
  countries: {
    code: string;
    label: string;
    counts: (number | null)[];
    /** AN-F5, the map's pop-up, by the end of months[i] (null where counts[i] is): women of those who said woman or man, */
    womenPct?: (number | null)[];
    /** the events its people had come to, */
    events?: (number | null)[];
    /** and their organisations. */
    organisations?: (number | null)[];
  }[];
};

/** A8: the first people (most events first) and how many there are in all. */
export type AnalyticsGoneQuiet = {
  total: number;
  people: { key: string; name: string; contactId: string | null; organisation: string | null; events: number; lastSeen: string }[];
};

/** A9: the stakeholder type groups a target can be set for (as "Who takes part" groups them). */
export const ANALYTICS_TYPE_GROUPS = [
  { key: "government", label: "Government" },
  { key: "civil_society", label: "Civil society" },
  { key: "academia", label: "Academia" },
  { key: "media_other", label: "Media and other" },
] as const;
export type AnalyticsTypeGroup = (typeof ANALYTICS_TYPE_GROUPS)[number]["key"];

/** A9: share now and target (null = no target set) per line. */
export type AnalyticsBalance = {
  women: { pct: number | null; target: number | null };
  types: { key: AnalyticsTypeGroup; label: string; pct: number | null; target: number | null }[];
  /** The partner countries reached (somebody took part); null while no partner countries are set. */
  countries: { reached: number; of: number } | null;
};

/** A10: what is missing, each with where to act on it. */
export type AnalyticsNotReached = {
  countries: { code: string; label: string; people: number }[];
  types: { key: AnalyticsTypeGroup; label: string; pct: number | null; target: number }[];
};

/** AN-F3: an event held in a month, for the bar's pop-up. */
export type AnalyticsMonthEvent = { title: string; code: string | null; path: string | null };

/**
 * AN-F2: what a day of "The year at a glance" holds, for its pop-up: an activity of that day (code, title,
 * place, type; path opens it) or a report deadline (path opens the reports).
 */
export type AnalyticsYearItem = { kind: "event" | "deadline"; title: string; code: string | null; place: string | null; type: string | null; path: string | null };

/** A11: one calendar year; only the days that hold something. items: the same, in full (AN-F2). */
export type AnalyticsYear = {
  year: number;
  days: { date: string; events: string[]; deadlines: string[]; items?: AnalyticsYearItem[] }[];
};

/** AN-F7: one reading of a journey: when, the value, who entered it and how it was obtained; path opens what it came from. */
export type AnalyticsJourneyReading = {
  date: string;
  value: number;
  confirmed: boolean;
  /** The event the reading came from, if any. */
  event: string | null;
  /** How the value was obtained ("Feedback forms", "Ministry statistics"), else the indicator's means of verification; "" = not recorded. */
  source?: string;
  /** Who entered it. */
  by?: string;
  /** The event's activity, else the results page. */
  path?: string | null;
};

/** A12: one indicator's journey. Values in the indicator's unit. */
export type AnalyticsJourney = {
  id: string;
  code: string;
  name: string;
  unit: string;
  baseline: number | null;
  target: number | null;
  targetDate: string | null;
  readings: AnalyticsJourneyReading[];
  /** AN-F7: the day the baseline stands for (1 January of its year, else the project's start); null = not known. */
  baselineDate?: string | null;
  /** AN-F7: every target with a date (mid-term and final), in date order; the final one last. */
  targets?: { label: string; value: number; date: string; final: boolean }[];
  /** AN-F7: the target is below the baseline: the chart reads downwards and says so. */
  lowerIsBetter?: boolean;
  /** AN-F7: the indicator's status today (on track, behind, not confirmed), as on Results. */
  status?: StatusChip;
  /** AN-F7: the result it measures, for the selector's groups; null = not linked to one. */
  result?: { id: string; code: string; label: string } | null;
  /** "14%" / "5 agencies", the latest value with its unit, and the target the same way (as Results writes them). */
  currentText?: string;
  targetText?: string;
};

/** A13: one series (a line of work's trainings) and the score of each edition. */
export type AnalyticsTrainingSeries = {
  id: string;
  name: string;
  editions: { id: string; title: string; date: string; participants: number; satisfaction: number | null; responses: number }[];
  /** Average score of people who had come to an event of the project before, and of first-timers; null = no answers. */
  cameBefore: number | null;
  firstTime: number | null;
};

/**
 * AN-F9: one kind of event in "Cost per event" (Budget insights). Counted only over the events that took place,
 * with somebody on the attendance list and a final (or at least approved) cost.
 */
export type BudgetInsightKind = {
  kind: import("../enums").EventKind;
  label: string;
  events: number;
  participants: number;
  costCents: number;
  costPerParticipantCents: number | null;
};

/** AN-F9: why an event is left out of the cost per participant. */
export const COST_LEFT_OUT_REASONS = {
  not_held: "Not held yet",
  cancelled: "Cancelled",
  no_attendance: "Nobody on the attendance list",
  no_cost: "No final or approved cost yet",
  /** X-43 (audit round 4): held, but outside the period picked; named so no held event goes missing. */
  other_period: "Held in another period",
} as const;
export type CostLeftOutReason = keyof typeof COST_LEFT_OUT_REASONS;

/** AN-F8/AN-F9: one event in "Cost per event". costCents: the final cost once closed, else the approved ToR (null: neither yet). */
export type BudgetInsightEvent = {
  id: string;
  kind: import("../enums").EventKind;
  title: string;
  code: string | null;
  startDate: string;
  path: string | null;
  participants: number;
  /**
   * "final": the event was closed at its final cost; "spent" (AR-F3, audit round 4): held, not closed yet, with costs
   * logged, so what is spent so far (not the ToR estimate); "approved": the funder approved its ToR; null: neither yet.
   */
  costBasis: "final" | "spent" | "approved" | null;
  costCents: number | null;
  costPerParticipantCents: number | null;
  /** Null: counted in the averages; otherwise why it is left out. */
  leftOut: CostLeftOutReason | null;
};

/**
 * AN-F8 (Marius, 1 Nov): the budget analysis that moved from Analytics to Administration › Budget ›
 * "Budget insights". GET /p/:projectId/analytics/budget-insights?from=&to= (the Budget tab's period).
 */
export type BudgetInsights = {
  /** How much of the project's time has gone (0 to 100); null without start and end dates. Merged into Spending pace. */
  timePct: number | null;
  /** AN-F9: cost per event, per kind and event by event (the period's events). */
  costPerEvent: { kinds: BudgetInsightKind[]; events: BudgetInsightEvent[] };
  /**
   * AN-F9: expert days by result. used: days on signed or approved timesheets on the result's activities (as "days used"
   * everywhere). planned: per activity and expert, the days in the activity's ToR when the ToR names the expert, else the
   * days of the expert's assignment to the activity (one or the other, never both). null: nothing planned.
   */
  expertDays: { code: string | null; label: string; used: number; planned: number | null }[];
};
export const budgetInsightsQuery = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});

/** AN6: the figures whose people open in a list (A8: "quiet", the people who have gone quiet). */
export const ANALYTICS_PEOPLE_FIGURES = ["engaged", "women", "returning", "quiet"] as const;
export type AnalyticsPeopleFigure = (typeof ANALYTICS_PEOPLE_FIGURES)[number];

/** AN6: GET /p/:projectId/analytics/people, the people behind a figure (the team only). */
export type AnalyticsPeople = {
  figure: AnalyticsPeopleFigure;
  title: string;
  periodLabel: string;
  /** "Serbia", "Ministry of Interior": the filter from a returning row, if any. */
  filterLabel: string | null;
  people: { key: string; name: string; contactId: string | null; organisation: string | null; country: string | null; events: number }[];
};
